/**
 * Rendering pipeline for Paper2Ink vector editor.
 * Converts CIM ink objects into SVG path commands and canvas rendering.
 */

import type { CIMDocument, CompoundFill, CenterlineStroke, InkObject } from './model.ts';
import { computeObjectBounds } from './selection.ts';

export function renderObjectToSVGPath(obj: InkObject): string {
  if (obj.kind === 'compound_fill') {
    const parts: string[] = [];
    for (const ring of obj.rings) {
      if (ring.points.length === 0) continue;
      parts.push(`M ${ring.points[0][0]},${ring.points[0][1]}`);
      for (let i = 1; i < ring.points.length; i++) {
        parts.push(`L ${ring.points[i][0]},${ring.points[i][1]}`);
      }
      parts.push('Z');
    }
    return parts.join(' ');
  }

  if (obj.kind === 'centerline_stroke') {
    if (obj.points.length === 0) return '';
    const parts: string[] = [`M ${obj.points[0][0]},${obj.points[0][1]}`];
    for (let i = 1; i < obj.points.length; i++) {
      parts.push(`L ${obj.points[i][0]},${obj.points[i][1]}`);
    }
    return parts.join(' ');
  }

  return '';
}

export interface RenderOptions {
  selectedIds?: ReadonlySet<string>;
  selectionColor?: string;
  selectionHaloWidth?: number;
}

export function renderToCanvas(
  doc: CIMDocument,
  ctx: CanvasRenderingContext2D,
  options?: RenderOptions
): void {
  const selected = options?.selectedIds;
  const selColor = options?.selectionColor || '#0066ff';

  ctx.save();
  ctx.clearRect(0, 0, doc.page.width, doc.page.height);

  for (const obj of doc.objects) {
    ctx.save();
    const [a, b, c, d, e, f] = obj.transform;
    ctx.setTransform(a, b, c, d, e, f);

    const isSelected = selected && selected.has(obj.id);
    const path2d = new Path2D(renderObjectToSVGPath(obj));

    if (obj.kind === 'compound_fill') {
      ctx.globalAlpha = obj.opacity;
      ctx.fillStyle = obj.color;
      ctx.fill(path2d, obj.fill_rule);

      if (isSelected) {
        ctx.strokeStyle = selColor;
        ctx.lineWidth = 2.0;
        ctx.stroke(path2d);
      }
    } else if (obj.kind === 'centerline_stroke') {
      ctx.globalAlpha = obj.opacity;
      ctx.strokeStyle = obj.color;
      ctx.lineWidth = obj.width;
      ctx.lineCap = 'round';
      ctx.lineJoin = 'round';
      ctx.stroke(path2d);

      if (isSelected) {
        ctx.strokeStyle = selColor;
        ctx.lineWidth = obj.width + 4.0;
        ctx.globalAlpha = 0.4;
        ctx.stroke(path2d);
      }
    }

    ctx.restore();
  }

  ctx.restore();
}
